import type { SettingsClientCache } from '../types/settings';
import { clearRequestPacing } from './requestPacing';

const VERSION_KEY = 'zeettu_settings_version_id';
const ENCRYPTED_KEY = 'zeettu_settings_encrypted_v1';
const CRYPTO_KEY_STORAGE = 'zeettu_settings_crypto_key';
const LEGACY_ACCESS_CACHE_KEY = 'zeettu_settings_access_v1';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function getOrCreateDeviceKey(): Promise<CryptoKey> {
  let raw = localStorage.getItem(CRYPTO_KEY_STORAGE);
  if (!raw) {
    const keyBytes = crypto.getRandomValues(new Uint8Array(32));
    raw = bytesToBase64(keyBytes);
    localStorage.setItem(CRYPTO_KEY_STORAGE, raw);
  }
  const keyData = base64ToBytes(raw);
  return crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export function readPlainVersionId(): string | null {
  return localStorage.getItem(VERSION_KEY);
}

function writePlainVersionId(versionId: string): void {
  localStorage.setItem(VERSION_KEY, versionId);
}

export async function encryptJson(payload: unknown): Promise<string> {
  const key = await getOrCreateDeviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return JSON.stringify({
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  });
}

export async function decryptJson<T>(stored: string): Promise<T> {
  const { iv, ciphertext } = JSON.parse(stored) as { iv: string; ciphertext: string };
  const key = await getOrCreateDeviceKey();
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBytes(iv) },
    key,
    base64ToBytes(ciphertext),
  );
  return JSON.parse(new TextDecoder().decode(decrypted)) as T;
}

export async function saveSettingsClientCache(cache: SettingsClientCache): Promise<void> {
  writePlainVersionId(cache.versionId);
  const encrypted = await encryptJson(cache);
  localStorage.setItem(ENCRYPTED_KEY, encrypted);
}

export async function readSettingsClientCache(): Promise<SettingsClientCache | null> {
  const raw = localStorage.getItem(ENCRYPTED_KEY);
  if (!raw) return null;
  try {
    return await decryptJson<SettingsClientCache>(raw);
  } catch {
    return null;
  }
}

export function clearSettingsCache(): void {
  localStorage.removeItem(VERSION_KEY);
  localStorage.removeItem(ENCRYPTED_KEY);
  localStorage.removeItem(CRYPTO_KEY_STORAGE);
  sessionStorage.removeItem(LEGACY_ACCESS_CACHE_KEY);
  clearRequestPacing();
}
